// AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md
import { useEffect, useState } from 'react'
import { db } from '../firebase/config'
import { client } from './Client'
import notDeleted from '../helpers/notDeleted'

// Wat de rekenregels nodig hebben voor de tijdshorizon: de Traject-velden van
// elke deelnemer en de standaard van de organisatie. Nodig omdat zowel de
// €-regel op elke effectgrafiek als het organisatietotaal en Impact per euro
// het lezen; een listener per grafiek zou per lijstitem een volledige
// Personas-listener openen (risico 11). Daarom één `.get()` per organisatie,
// kort gedeeld tussen mounts: na CACHE_MS haalt de volgende mount opnieuw op,
// zodat een gewijzigde startdatum of uren niet een hele sessie oud blijft.
const CACHE_MS = 60 * 1000

/** @type {Map<string, {at: number, promise: Promise<InterventionDoseContext>}>} */
const cache = new Map()

/**
 * @typedef {object} InterventionDoseContext
 * @property {Object<string, object>} personasById - Personas-docs op ID (niet verwijderd)
 * @property {{ Months?: *, HoursPerWeek?: * }|null} orgDefault - CompagnyMeta.DefaultInterventionDose
 */

/** @returns {Promise<InterventionDoseContext>} */
const fetchContext = async () => {
    const [personasSnap, metaSnap] = await Promise.all([
        db.collection('Personas').where('CompagnyID', '==', client).get(),
        db.collection('CompagnyMeta').where('CompagnyID', '==', client).get(),
    ])
    const personasById = {}
    personasSnap.docs.map((d) => d.data()).filter(notDeleted).forEach((p) => {
        if (p.ID) personasById[p.ID] = p
    })
    const meta = metaSnap.docs.map((d) => d.data()).filter(notDeleted)[0]
    return { personasById, orgDefault: meta?.DefaultInterventionDose || null }
}

/** @returns {Promise<InterventionDoseContext>} */
export const fetchInterventionDoseContext = () => {
    const hit = cache.get(client)
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise
    const promise = fetchContext().catch((err) => {
        // Een mislukte read niet bewaren — de volgende mount probeert opnieuw.
        cache.delete(client)
        throw err
    })
    cache.set(client, { at: Date.now(), promise })
    return promise
}

/**
 * Gooit de gedeelde cache weg, zodat de volgende mount opnieuw leest. Aangeroepen
 * na het opslaan van de standaard (InterventionDoseSettings): de ladder leest die
 * live, de MSI-schermen via deze cache — zonder legen rekenen ze tot een minuut
 * lang met een verschillende standaard.
 */
export const invalidateInterventionDoseCache = () => cache.clear()

export const clearInterventionDoseCacheForTests = invalidateInterventionDoseCache

/**
 * @param {{ enabled?: boolean }} [options]
 * @returns {{ context: InterventionDoseContext|null, loading: boolean, error: Error|null }}
 */
const useInterventionDoseContext = ({ enabled = true } = {}) => {
    const [state, setState] = useState({ context: null, loading: true, error: null })

    useEffect(() => {
        if (!enabled) return
        let cancelled = false
        fetchInterventionDoseContext()
            .then((context) => { if (!cancelled) setState({ context, loading: false, error: null }) })
            .catch((error) => {
                console.error('useInterventionDoseContext error:', error)
                if (!cancelled) setState({ context: null, loading: false, error })
            })
        return () => { cancelled = true }
    }, [enabled])

    return state
}

export default useInterventionDoseContext
